/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from './icons';
import { useTranslation } from '../i18n/useTranslation';
import { CarouselItem } from '../data/carouselData';
import { homeService } from '../services/homeService';
import { getMultiCdnUrl } from '../utils/imageUtils';

const DoubleCarousel: React.FC = () => {
    const { t } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [items, setItems] = useState<CarouselItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Load items on mount
    useEffect(() => {
        const loadAllItems = async () => {
            setIsLoading(true);
            try {
                // Get base items from homeService (includes local overrides)
                const baseItems = await homeService.getCarouselItems();
                
                // Also fetch dynamic instagram items
                const { fetchInstagramCarouselItems } = await import('../data/carouselData');
                const instagramItems = await fetchInstagramCarouselItems();
                
                setItems([...baseItems, ...instagramItems]);
            } catch (error) {
                console.error('Failed to load carousel items:', error);
                setItems(await homeService.getCarouselItems());
            } finally {
                setIsLoading(false);
            }
        };
        loadAllItems();
    }, []);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const scrollAmount = 400;
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleItemClick = (item: CarouselItem) => {
        if (item.link) {
            window.open(item.link, '_blank', 'noopener,noreferrer');
        }
    };

    // Split items into two rows
    const row1 = items.slice(0, Math.ceil(items.length / 2));
    const row2 = items.slice(Math.ceil(items.length / 2));

    const renderCard = (item: CarouselItem, index: number, rowOffset: number = 0) => (
        <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: (index + rowOffset) * 0.1 }}
            onClick={() => handleItemClick(item)}
            className="group relative w-[320px] h-[420px] rounded-xl overflow-hidden cursor-pointer flex-shrink-0"
        >
            <img
                src={getMultiCdnUrl(item.imageUrl, 'BLOG')}
                alt={item.source === 'instagram' ? item.titleKey : t(item.titleKey)}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100 group-hover:opacity-100 transition-opacity" />

            {/* Category Badge */}
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-900">
                    {item.category}
                </span>
            </div>

            {/* Title */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-xl font-serif font-bold text-white mb-2">
                    {item.source === 'instagram' ? item.titleKey.slice(0, 60) : t(item.titleKey)}
                </h3>
                <button className="text-sm font-semibold text-white/90 hover:text-white uppercase tracking-wider underline">
                    {t('carousel_view_more')}
                </button>
            </div>
        </motion.div>
    );

    return (
        <section className="py-16 bg-white dark:bg-[#18181B] overflow-hidden relative group/carousel">
            <div className="max-w-7xl mx-auto px-6">
                <div className="mb-8 text-center">
                    <span className="text-xs font-bold uppercase tracking-[0.3em] text-gray-400 dark:text-[#C0A172] block mb-2">
                        {t('carousel_section_label')}
                    </span>
                    <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1]">
                        {t('carousel_section_title')}
                    </h2>
                </div>

                {isLoading && (
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                    </div>
                )}

                {!isLoading && (
                    <div className="relative">
                        {/* Left Arrow */}
                        <button
                            onClick={() => scroll('left')}
                            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 dark:bg-gray-800/90 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-white dark:hover:bg-gray-700 -ml-4"
                            aria-label="Previous"
                        >
                            <ChevronLeftIcon className="w-6 h-6 text-gray-900 dark:text-white" />
                        </button>

                        {/* Scrollable Container */}
                        <div
                            ref={scrollRef}
                            className="overflow-x-auto scrollbar-hide scroll-smooth"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            <div className="flex flex-col gap-4 min-w-max">
                                {/* Row 1 */}
                                <div className="flex gap-4">
                                    {row1.map((item, index) => renderCard(item, index, 0))}
                                </div>

                                {/* Row 2 */}
                                {row2.length > 0 && (
                                    <div className="flex gap-4">
                                        {row2.map((item, index) => renderCard(item, index, row1.length))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Arrow */}
                        <button
                            onClick={() => scroll('right')}
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 dark:bg-gray-800/90 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-white dark:hover:bg-gray-700 -mr-4"
                            aria-label="Next"
                        >
                            <ChevronRightIcon className="w-6 h-6 text-gray-900 dark:text-white" />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default DoubleCarousel;
